import { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BarChart3, CreditCard, Download, FileText, Globe2, Home, Image, LayoutDashboard, LogOut, Mail, Menu, Package, RotateCcw, ScrollText, Settings, ShoppingCart, Store, Tags, Ticket, Truck, Users, X } from "lucide-react";
import { api } from "../../lib/api";

const AdminContext = createContext(null);
export const useAdmin = () => useContext(AdminContext);

const NAV_GROUPS = [
  {
    title: "YÖNETİM",
    items: [
      { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/admin/urunler", label: "Ürünler", icon: Package },
      { to: "/admin/kategoriler", label: "Kategoriler & Markalar", icon: Tags },
      { to: "/admin/siparisler", label: "Siparişler", icon: ShoppingCart },
      { to: "/admin/iadeler", label: "İade Talepleri", icon: RotateCcw },
      { to: "/admin/teklifler", label: "Teklif Talepleri", icon: FileText },
      { to: "/admin/musteriler", label: "Müşteriler", icon: Users },
      { to: "/admin/faturalar", label: "Faturalar", icon: FileText },
    ],
  },
  {
    title: "PAZARYERLERİ",
    items: [
      { to: "/admin/marketplace", label: "Pazaryeri Merkezi", icon: Globe2 },
      { to: "/admin/tedarikciler", label: "Tedarikçiler (XML)", icon: Truck },
    ],
  },
  {
    title: "ÖDEME",
    items: [
      { to: "/admin/odeme-yontemleri", label: "Ödeme Yöntemleri", icon: CreditCard },
      { to: "/admin/odeme-loglari", label: "Ödeme Logları", icon: ScrollText },
    ],
  },
  {
    title: "SİSTEM",
    items: [
      { to: "/admin/ayarlar", label: "Genel Ayarlar", icon: Settings },
      { to: "/admin/pazarlama", label: "Kupon & Kampanya", icon: Ticket },
      { to: "/admin/anasayfa", label: "Ana Sayfa Yönetimi", icon: Home },
      { to: "/admin/medya", label: "Medya Kütüphanesi", icon: Image },
      { to: "/admin/bannerlar", label: "Bannerlar", icon: Image },
      { to: "/admin/raporlar", label: "Raporlar", icon: BarChart3 },
      { to: "/admin/epostalar", label: "E-postalar", icon: Mail },
      { to: "/admin/aktarim", label: "İçe/Dışa Aktar", icon: Download },
      { to: "/admin/entegrasyon-loglari", label: "Entegrasyon Logları", icon: ScrollText },
    ],
  },
];

export default function AdminLayout() {
  const [admin, setAdmin] = useState(null); // null=checking
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.get("/admin/auth/me")
      .then(({ data }) => setAdmin(data))
      .catch(() => navigate("/admin/giris"));
  }, [navigate, location.pathname]);

  if (!admin) {
    return <div className="min-h-screen bg-[#0F1115] flex items-center justify-center">
      <div className="w-9 h-9 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  const logout = async () => {
    await api.post("/admin/auth/logout");
    navigate("/admin/giris");
  };

  const Sidebar = (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-[#272B33] flex items-center gap-2.5">
        <span className="bg-white rounded-lg px-2.5 py-2 inline-flex">
          <img src="/assets/bigeldi-logo.png" alt="BIGELDİ" className="h-6 w-auto object-contain" />
        </span>
        <span className="text-[10px] font-mono-ed text-neutral-500">ADMIN</span>
      </div>
      <nav className="flex-1 p-3 space-y-4 overflow-y-auto" data-testid="admin-nav">
        {NAV_GROUPS.map((g) => (
          <div key={g.title}>
            <p className="px-3.5 pb-1.5 text-[10px] font-mono-ed font-bold tracking-[0.15em] text-neutral-600">{g.title}</p>
            <div className="space-y-0.5">
              {g.items.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMenuOpen(false)}
                  className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-blue-600/15 text-blue-400" : "text-neutral-400 hover:bg-[#1E222A] hover:text-white"}`}
                  data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  <n.icon size={17} /> {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-[#272B33] space-y-1">
        <NavLink to="/" className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-neutral-400 hover:bg-[#1E222A] hover:text-white" data-testid="admin-back-to-store">
          <Store size={17} /> Mağazaya Dön
        </NavLink>
        <button onClick={logout} className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-rose-400 hover:bg-rose-500/10" data-testid="admin-logout">
          <LogOut size={17} /> Çıkış
        </button>
      </div>
    </div>
  );

  return (
    <AdminContext.Provider value={{ admin }}>
      <Helmet><title>Yönetim | BIGELDİ</title></Helmet>
      <div className="min-h-screen bg-[#0F1115] text-neutral-200 flex">
        <aside className="hidden lg:block w-64 shrink-0 bg-[#13161B] border-r border-[#272B33] fixed inset-y-0">{Sidebar}</aside>
        {menuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />
            <aside className="absolute left-0 inset-y-0 w-72 bg-[#13161B] border-r border-[#272B33]">{Sidebar}</aside>
          </div>
        )}
        <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
          <header className="h-16 bg-[#13161B]/80 backdrop-blur border-b border-[#272B33] flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30">
            <button className="lg:hidden p-2" onClick={() => setMenuOpen(true)} aria-label="Menü" data-testid="admin-mobile-menu"><Menu size={20} /></button>
            <div className="flex items-center gap-3 ml-auto">
              <div className="text-right">
                <p className="text-sm font-semibold text-white leading-tight">{admin.name}</p>
                <p className="text-[10px] font-mono-ed text-blue-400">{admin.role}</p>
              </div>
              <div className="h-9 w-9 rounded-full bg-blue-600/20 text-blue-400 flex items-center justify-center font-bold text-sm">
                {admin.name?.[0]?.toLocaleUpperCase("tr-TR")}
              </div>
            </div>
          </header>
          <main className="flex-1 p-4 sm:p-6 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </AdminContext.Provider>
  );
}
